import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { useDebugEnabled } from '@/debug/debugPreference';
import './RoverDebugOverlay.css';

/**
 * Debug overlay to visualize avatar and rover positions
 * Shows current player position, rover count, and owned plots
 */
export const RoverDebugOverlay: React.FC = () => {
  const game = useSelector((state: RootState) => state.game.currentGame);
  const user = useSelector((state: RootState) => state.auth.user);
  const debugOn = useDebugEnabled();

  if (!debugOn || !game || game.phase !== 'development') {
    return null;
  }

  // Find current player
  const currentPlayer = game.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);

  if (!currentPlayer) {
    return null;
  }

  // Get player data
  const position = currentPlayer.position || { q: 0, r: 0, s: 0 };
  const rovers = currentPlayer.rovers || 0;
  const deployedRovers = currentPlayer.deployedRovers || [];
  const ownedPlots = currentPlayer.ownedPlots || [];

  // Calculate adjacent hex for rover (one hex to the right of avatar)
  const roverPosition = {
    q: position.q + 1,
    r: position.r,
    s: position.s - 1
  };

  return (
    <div className="rover-debug-overlay">
      <div className="rover-debug-header">🎮 ROVER DEBUG MODE</div>
      
      <div className="rover-debug-section">
        <div className="rover-debug-title">📍 Avatar Position</div>
        <div className="rover-debug-value">
          Hex: ({position.q}, {position.r}, {position.s})
        </div>
        <div className="rover-debug-hint">
          → Avatar should be visible at base (center)
        </div>
      </div>

      <div className="rover-debug-section">
        <div className="rover-debug-title">🚗 Rover Status</div>
        <div className="rover-debug-value">
          Available: {rovers - deployedRovers.length} / {rovers}
        </div>
        <div className="rover-debug-value">
          Deployed: {deployedRovers.length}
        </div>
        {rovers > deployedRovers.length && (
          <div className="rover-debug-hint">
            → Rover should be at: ({roverPosition.q}, {roverPosition.r}, {roverPosition.s})
          </div>
        )}
      </div>

      <div className="rover-debug-section">
        <div className="rover-debug-title">🗺️ Owned Plots</div>
        <div className="rover-debug-value">
          Plots: {ownedPlots.join(', ')}
        </div>
        <div className="rover-debug-hint">
          → Click owned plot to deploy rover
        </div>
      </div>

      <div className="rover-debug-section">
        <div className="rover-debug-title">⏱️ Timer</div>
        <div className="rover-debug-value">
          {game.timer?.remainingSeconds || 0}s remaining
        </div>
      </div>

      <div className="rover-debug-instructions">
        <div className="rover-debug-title">🎯 Next Steps:</div>
        <ol>
          <li>Avatar sprite should appear at base (0,0,0)</li>
          <li>Rover sprite should appear adjacent to avatar</li>
          <li>Click any hex to move avatar there</li>
          <li>Rover should follow avatar automatically</li>
          <li>Click owned plot to deploy rover</li>
        </ol>
      </div>
    </div>
  );
};

